import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactElement } from 'react';
import { isoDateTime, type Project, type ScheduleFrom } from '@bigint-project/core';

import type { ProjectMetaPatch } from '../../store/commands';

interface ProjectInfoDialogProps {
  readonly isOpen: boolean;
  readonly project: Project;
  readonly allowProjectRename?: boolean;
  readonly onCancel: () => void;
  readonly onSave: (patch: ProjectMetaPatch) => void;
}

interface FormValues {
  name: string;
  scheduleFrom: ScheduleFrom;
  startDate: string;
  finishDate: string;
  statusDate: string;
  currency: string;
  notes: string;
}

function datePart(value: string | null): string {
  return value?.slice(0, 10) ?? '';
}

function replaceDate(value: string, date: string): ReturnType<typeof isoDateTime> {
  const time = value.slice(10) || 'T00:00:00';
  return isoDateTime(`${date}${time}`);
}

function makeFormValues(project: Project): FormValues {
  return {
    name: project.name,
    scheduleFrom: project.scheduleFrom,
    startDate: datePart(project.startDate),
    finishDate: datePart(project.finishDate),
    statusDate: datePart(project.statusDate),
    currency: project.currency,
    notes: project.notes ?? '',
  };
}

export function ProjectInfoDialog({
  isOpen,
  project,
  allowProjectRename = true,
  onCancel,
  onSave,
}: ProjectInfoDialogProps): ReactElement | null {
  const [values, setValues] = useState<FormValues>(() => makeFormValues(project));
  const [errors, setErrors] = useState<Partial<Record<'name' | 'anchorDate' | 'currency', string>>>({});
  const titleId = useId();
  const dialogRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setValues(makeFormValues(project));
      setErrors({});
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, project]);

  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    nameInputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const anchorField = values.scheduleFrom === 'ProjectStart' ? 'startDate' : 'finishDate';
  const anchorLabel = values.scheduleFrom === 'ProjectStart' ? '專案開始日' : '專案完成日';

  const update = <K extends keyof FormValues>(key: K, value: FormValues[K]): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors: Partial<Record<'name' | 'anchorDate' | 'currency', string>> = {};
    if (allowProjectRename && !values.name.trim()) nextErrors.name = '請輸入專案名稱。';
    if (!values[anchorField]) nextErrors.anchorDate = `請選擇${anchorLabel}。`;
    if (!values.currency.trim()) nextErrors.currency = '請輸入幣別。';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onSave({
      name: allowProjectRename ? values.name.trim() : project.name,
      scheduleFrom: values.scheduleFrom,
      startDate: replaceDate(project.startDate, values.startDate),
      finishDate: replaceDate(project.finishDate, values.finishDate),
      statusDate: values.statusDate ? replaceDate(project.statusDate ?? project.startDate, values.statusDate) : null,
      currency: values.currency.trim().toUpperCase(),
      notes: values.notes,
    });
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const items = [...focusable];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <form
        aria-labelledby={titleId}
        aria-modal="true"
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900"
        ref={dialogRef}
        onKeyDown={trapFocus}
        onSubmit={handleSubmit}
        role="dialog"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id={titleId} className="text-base font-semibold">專案資訊</h2>
          <button type="button" onClick={onCancel} aria-label="關閉專案資訊" className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">關閉</button>
        </div>

        <div className="grid gap-3 text-sm">
          <label className="grid gap-1">
            專案名稱
            <input disabled={!allowProjectRename} ref={nameInputRef} aria-invalid={allowProjectRename && Boolean(errors.name)} aria-describedby={allowProjectRename && errors.name ? 'project-name-error' : undefined} value={values.name} onChange={(event) => update('name', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600" />
            {allowProjectRename && errors.name ? <span id="project-name-error" className="text-xs text-red-600">{errors.name}</span> : null}
          </label>
          <label className="grid gap-1">
            排程方向
            <select value={values.scheduleFrom} onChange={(event) => update('scheduleFrom', event.target.value as ScheduleFrom)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600">
              <option value="ProjectStart">從專案開始日排程</option>
              <option value="ProjectFinish">從專案完成日排程</option>
            </select>
          </label>
          <label className="grid gap-1">
            {anchorLabel}
            <input type="date" aria-invalid={Boolean(errors.anchorDate)} aria-describedby={errors.anchorDate ? 'project-anchor-date-error' : undefined} value={values[anchorField]} onChange={(event) => update(anchorField, event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
            {errors.anchorDate ? <span id="project-anchor-date-error" className="text-xs text-red-600">{errors.anchorDate}</span> : null}
          </label>
          <label className="grid gap-1">
            狀態日（留白代表今天）
            <input type="date" value={values.statusDate} onChange={(event) => update('statusDate', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
          </label>
          <label className="grid gap-1">
            幣別
            <input aria-invalid={Boolean(errors.currency)} aria-describedby={errors.currency ? 'project-currency-error' : undefined} value={values.currency} onChange={(event) => update('currency', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
            {errors.currency ? <span id="project-currency-error" className="text-xs text-red-600">{errors.currency}</span> : null}
          </label>
          <label className="grid gap-1">
            備註
            <textarea value={values.notes} onChange={(event) => update('notes', event.target.value)} rows={3} className="resize-y rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800">取消</button>
          <button type="submit" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">儲存</button>
        </div>
      </form>
    </div>
  );
}
